import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { STORAGE_KEYS } from '@/constants/app'
import { useLocalStorage } from '@/hooks'
import { i18n } from '@/lang'
import type { Language } from '@/types/common'

interface I18nContextValue {
  lang: Language
  setLang: (lang: Language) => void
  toggleLang: () => void
  t: typeof i18n.en
  fontClass: 'font-en' | 'font-zh'
  locale: 'en-US' | 'zh-TW'
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

interface I18nProviderProps {
  children: ReactNode
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const [lang, setLang] = useLocalStorage<Language>(STORAGE_KEYS.LANGUAGE, 'en')

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-Hant' : 'en'
  }, [lang])

  const value: I18nContextValue = {
    lang,
    setLang,
    toggleLang: () => setLang(current => current === 'en' ? 'zh' : 'en'),
    t: i18n[lang],
    fontClass: lang === 'zh' ? 'font-zh' : 'font-en',
    locale: lang === 'zh' ? 'zh-TW' : 'en-US',
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18nContext = () => {
  const context = useContext(I18nContext)

  if (!context) {
    throw new Error('useI18n 必須在 I18nProvider 內部使用')
  }

  return context
}